<script lang="ts">
import { defineBlock } from "@bagelink/blox"
import { $, defineSchema } from "@bagelink/vue"
const schema = defineSchema({
  anchor: $.text("Section anchor"),
  layout: $.select(
    "Layout",
    [
      { label: "Media only", value: "full" },
      { label: "Media then text", value: "media-start" },
      { label: "Text then media", value: "media-end" },
    ],
    { default: "full" },
  ),
  kind: $.select(
    "Media type",
    [
      { label: "Image", value: "image" },
      { label: "Video", value: "video" },
    ],
    { default: "image" },
  ),
  media: $.upload("Image", { accept: "image/*", fill: true }).if(
    'kind eq "image"',
  ),
  videoSource: $.select(
    "Video source",
    [
      { label: "YouTube, Vimeo, or external URL", value: "url" },
      { label: "Uploaded video", value: "upload" },
    ],
    { default: "url" },
  ).if('kind eq "video"'),
  videoUrl: $.url("Video URL").if('kind eq "video" and videoSource eq "url"'),
  videoFile: $.upload("Video file", { accept: "video/*" }).if(
    'kind eq "video" and videoSource eq "upload"',
  ),
  mobileMedia: $.upload("Mobile image", { accept: "image/*" }).if(
    'kind eq "image" and media pr',
  ),
  alt: $.text("Media description").if('kind eq "image"'),
  caption: $.text("Caption"),
  title: $.text("Heading").if('layout ne "full"'),
  specialHeading: $.checkbox("Use special heading font", { default: true }).if(
    'layout ne "full" and title pr',
  ),
  body: $.text("Text", { multiline: true }).if('layout ne "full"'),
  width: $.select(
    "Section width",
    [
      { label: "Regular (1000px)", value: "regular" },
      { label: "Wide (1200px)", value: "wide" },
      { label: "Full width", value: "full" },
    ],
    { default: "wide" },
  ).if('layout ne "full"'),
  mediaWidth: $.select(
    "Media width",
    [
      { label: "Small (400px)", value: "small" },
      { label: "Medium (700px)", value: "medium" },
      { label: "Large (1000px)", value: "large" },
      { label: "Extra large (1400px)", value: "extra-large" },
      { label: "Full width", value: "full" },
    ],
    { default: "large" },
  ).if('layout eq "full"'),
  ratio: $.select(
    "Media ratio",
    [
      { label: "Natural", value: "natural" },
      { label: "Wide", value: "16-9" },
      { label: "Landscape", value: "4-3" },
      { label: "Square", value: "1" },
      { label: "Portrait", value: "3-4" },
    ],
    { default: "natural" },
  ),
  fit: $.select(
    "Image fit",
    [
      { label: "Contain", value: "contain" },
      { label: "Crop", value: "cover" },
    ],
    { default: "contain" },
  ).if('kind eq "image" and ratio ne "natural"'),
  rounded: $.checkbox("Rounded corners", { default: false }),
  controls: $.checkbox("Show video controls", { default: true }).if(
    'kind eq "video"',
  ),
  autoplay: $.checkbox("Autoplay video", { default: false }).if(
    'kind eq "video"',
  ),
  loop: $.checkbox("Loop video", { default: false }).if('kind eq "video"'),
  muted: $.checkbox("Mute video", { default: false }).if('kind eq "video"'),
  link: $.url("Optional image link").if('kind eq "image"'),
  linkLabel: $.text("Link button label", { default: "Learn more" }).if(
    'kind eq "image" and layout ne "full" and link pr',
  ),
  compact: $.checkbox("Compact spacing", { default: false }),
  surface: $.select(
    "Background",
    [
      { label: "Page background", value: "page" },
      { label: "White surface", value: "surface" },
      { label: "White card", value: "card" },
      { label: "Brand color", value: "brand" },
      { label: "Dark", value: "dark" },
      { label: "Image", value: "image" },
    ],
    { default: "page" },
  ),
  backgroundImage: $.upload("Background image").if('surface eq "image"'),
  backgroundFit: $.select(
    "Background image fit",
    [
      { label: "Cover", value: "cover" },
      { label: "Contain", value: "contain" },
      { label: "Fill", value: "fill" },
    ],
    { default: "cover" },
  ).if('surface eq "image" and backgroundImage pr'),
  dimBackground: $.checkbox("Dim background", { default: false }).if(
    'surface eq "image" and backgroundImage pr',
  ),
});
export const EventMedia = defineBlock({
  name: "EventMedia",
  label: "Media",
  icon: "perm_media",
  thumbnail: "/block-thumbs/EventMedia.svg",
  category: "Event",
  order: 70,
  schema,
})
</script>
<script setup lang="ts">
import { computed } from "vue";

import { BglVideo, Btn, Icon, pathKeyToURL } from "@bagelink/vue"


;

const props = withDefaults(
  defineProps<{
    anchor?: string;
    title?: string;
    specialHeading?: boolean;
    body?: string;
    kind?: "image" | "video";
    media?: string;
    videoSource?: "url" | "upload";
    videoUrl?: string;
    videoFile?: string;
    mobileMedia?: string;
    alt?: string;
    caption?: string;
    layout?: "full" | "media-start" | "media-end";
    width?: "full" | "wide" | "regular";
    mediaWidth?: "small" | "medium" | "large" | "extra-large" | "full";
    ratio?: "natural" | "16-9" | "4-3" | "1" | "3-4";
    fit?: "contain" | "cover";
    rounded?: boolean;
    controls?: boolean;
    autoplay?: boolean;
    loop?: boolean;
    muted?: boolean;
    link?: string;
    linkLabel?: string;
    compact?: boolean;
    surface?: "page" | "surface" | "card" | "brand" | "dark" | "image";
    backgroundImage?: string;
    backgroundFit?: "cover" | "contain" | "fill";
    dimBackground?: boolean;
  }>(),
  {
    anchor: "",
    title: "",
    specialHeading: true,
    body: "",
    kind: "image",
    media: "",
    videoSource: "url",
    videoUrl: "",
    videoFile: "",
    mobileMedia: "",
    alt: "",
    caption: "",
    layout: "full",
    width: "wide",
    mediaWidth: "large",
    ratio: "natural",
    fit: "contain",
    rounded: false,
    controls: true,
    autoplay: false,
    loop: false,
    muted: false,
    link: "",
    linkLabel: "Learn more",
    compact: false,
    surface: "page",
    backgroundImage: "",
    backgroundFit: "cover",
    dimBackground: false,
  },
);

const surfaceClass = computed(
  () =>
    ({
      page: "bg-bg",
      surface: "bg-box",
      card: "bg-bg",
      brand: "bg-primary color-white keep-white",
      dark: "bg-black color-white keep-white",
      image: "color-white keep-white",
    })[props.surface],
);
const sectionStyle = computed(() =>
  props.surface === "image" && props.backgroundImage
    ? {
        backgroundImage: `${props.dimBackground ? "linear-gradient(#0006, #0006), " : ""}url("${pathKeyToURL(props.backgroundImage) || props.backgroundImage}")`,
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundSize:
          props.backgroundFit === "fill" ? "100% 100%" : props.backgroundFit,
      }
    : undefined,
);
const widthClass = computed(
  () =>
    ({
      full: "w-100p",
      wide: "w-1200px",
      regular: "w-1000px",
    })[props.width],
);
const mediaWidthClass = computed(
  () =>
    ({
      small: "w-400px",
      medium: "w-700px",
      large: "w-1000px",
      "extra-large": "w-1400px",
      full: "w-100p",
    })[props.mediaWidth],
);
const containerClass = computed(() => [
  props.layout === "full" ? mediaWidthClass.value : widthClass.value,
  props.surface === "card" ? "bg-box shadow-light radius-2 p-1 m_p-075" : "",
  props.rounded || props.surface === "card" ? "overflow-hidden" : "",
  props.layout === "full"
    ? ""
    : "grid grid-wrap-2 gap-3 m_gap-1 align-items-center",
]);
const mediaUrl = computed(() => pathKeyToURL(props.media) || props.media);
const videoSrc = computed(() => {
  const source =
    props.videoSource === "upload" ? props.videoFile : props.videoUrl;
  return pathKeyToURL(source) || source || mediaUrl.value;
});
const mobileUrl = computed(
  () => pathKeyToURL(props.mobileMedia) || props.mobileMedia,
);
const ratioClass = computed(() =>
  props.ratio === "natural" ? "" : `ratio-${props.ratio}`,
);
const mediaClass = computed(() => [
  "w-100p block",
  ratioClass.value,
  props.fit,
  props.rounded ? "radius-1" : "",
]);
</script>

<template>
  <section
    :id="anchor || undefined"
    class="px-2 m_px-1"
    :class="[surfaceClass, compact ? 'py-1' : 'py-4 m_py-2']"
    :style="sectionStyle"
  >
    <div :class="containerClass">
      <div v-if="layout !== 'media-end'">
        <component
          :is="kind === 'image' && link ? 'a' : 'div'"
          :href="kind === 'image' ? link || undefined : undefined"
        >
          <picture v-if="kind === 'image' && media">
            <source
              v-if="mobileUrl"
              media="(max-width: 910px)"
              :srcset="mobileUrl"
            />
            <img
              :src="mediaUrl"
              :alt="alt"
              :class="mediaClass"
              loading="lazy"
            />
          </picture>
          <BglVideo
            v-else-if="kind === 'video' && videoSrc"
            :src="videoSrc"
            :title="title || caption || 'Video'"
            :aspect-ratio="
              ratio === 'natural' ? '16:9' : ratio.replace('-', ':')
            "
            :controls="controls"
            :autoplay="autoplay"
            :loop="loop"
            :mute="muted"
            playsinline
            :class="rounded ? 'overflow-hidden radius-1' : ''"
          />
          <div
            v-else
            class="w-100p ratio-16-9 bg-box border flex-center opacity-6"
            :class="rounded ? 'radius-1' : ''"
          >
            <Icon
              :name="kind === 'video' ? 'play_circle' : 'image'"
              :size="4"
              class="color-gray"
            />
          </div>
        </component>
        <p v-if="caption" class="txt-center txt14 m-0 pt-05">{{ caption }}</p>
      </div>

      <div
        v-if="title || body"
        :class="layout === 'full' ? 'w-800px txt-center pt-2' : ''"
      >
        <h2
          v-if="title"
          class="m-0 txt-48 m_txt-32 line-height-11 m_line-height-12 balance"
          :class="{ 'blox-special-heading': specialHeading }"
        >
          {{ title }}
        </h2>
        <p v-if="body" class="txt-20 pretty white-space-pre-wrap">{{ body }}</p>
        <Btn
          v-if="link && linkLabel && layout !== 'full'"
          :value="linkLabel"
          :href="link"
          icon-end="arrow_forward"
        />
      </div>

      <div v-if="layout === 'media-end'">
        <component
          :is="kind === 'image' && link ? 'a' : 'div'"
          :href="kind === 'image' ? link || undefined : undefined"
        >
          <picture v-if="kind === 'image' && media">
            <source
              v-if="mobileUrl"
              media="(max-width: 910px)"
              :srcset="mobileUrl"
            />
            <img
              :src="mediaUrl"
              :alt="alt"
              :class="mediaClass"
              loading="lazy"
            />
          </picture>
          <BglVideo
            v-else-if="kind === 'video' && videoSrc"
            :src="videoSrc"
            :title="title || caption || 'Video'"
            :aspect-ratio="
              ratio === 'natural' ? '16:9' : ratio.replace('-', ':')
            "
            :controls="controls"
            :autoplay="autoplay"
            :loop="loop"
            :mute="muted"
            playsinline
            :class="rounded ? 'overflow-hidden radius-1' : ''"
          />
          <div
            v-else
            class="w-100p ratio-16-9 bg-box border flex-center opacity-6"
            :class="rounded ? 'radius-1' : ''"
          >
            <Icon
              :name="kind === 'video' ? 'play_circle' : 'image'"
              :size="4"
              class="color-gray"
            />
          </div>
        </component>
        <p v-if="caption" class="txt-center txt14 m-0 pt-05">{{ caption }}</p>
      </div>
    </div>
  </section>
</template>
